import {
    List,
    ListItem,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
} from "@mui/material"
import { ReactElement } from "react"

type PageProps = {
    body: Array<{
        heading?: string
        subtitle?: string
        content?: string
        bullets?: string[]
        table?: {
            headers: string[]
            rows: string[]
        }
        link?: {
            text: string
            url: string
        }
    }>
}

/**
 * ExtendedTextBuilder is a component that renders a page with a title and a
 * policy. The policy is an array of objects, each object can have a heading,
 * content, bullets, and a table. The table is an object with headers and rows.
 *
 *
 * @example
 * <PrivacyPage />
 */
const ExtendedTextBuilder = ({ body }: PageProps): ReactElement => (
    <>
        {body.map((paragraph, idx) => {
            const key = `${paragraph.content?.slice(0, 30)}-${idx}`
            return (
                <Stack key={key}>
                    {paragraph.heading && (
                        <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
                            {paragraph.heading}
                        </Typography>
                    )}
                    {paragraph.subtitle && (
                        <Typography
                            variant="subtitle1"
                            fontWeight={500}
                            sx={{ mb: 2, mt: 3 }}
                        >
                            {paragraph.subtitle}
                        </Typography>
                    )}
                    {paragraph.content && (
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            {paragraph.content}
                        </Typography>
                    )}
                    {paragraph.bullets && (
                        <List sx={{ listStyleType: "disc", pl: 4 }}>
                            {paragraph.bullets.map((bullet) => (
                                <ListItem
                                    sx={{
                                        display: "list-item",
                                        padding: "4px 0", // Reduces vertical padding. Adjust the values as needed.
                                        "& .MuiListItemText-root": {
                                            margin: "0px", // Removes any default margin from the ListItemText component.
                                        },
                                    }}
                                    key={bullet.slice(0, 30)}
                                >
                                    <ListItemText>{bullet}</ListItemText>
                                </ListItem>
                            ))}
                        </List>
                    )}
                    {paragraph.link && (
                        <Typography
                            variant="body1"
                            sx={{ color: "primary.main", cursor: "pointer" }}
                            component="a"
                            href={paragraph.link.url}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {paragraph.link.text}
                        </Typography>
                    )}
                    {paragraph.table && (
                        <TableContainer
                            sx={{ mt: 2, mb: 4, border: "1px solid" }}
                        >
                            <Table>
                                <TableBody>
                                    {paragraph.table.headers.map(
                                        (header, rowIndex) => (
                                            <TableRow key={header}>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        sx={{ mb: 2 }}
                                                    >
                                                        {header}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ mb: 2 }}
                                                    >
                                                        {paragraph.table &&
                                                            paragraph.table
                                                                .rows[rowIndex]}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Stack>
            )
        })}
    </>
)

export default ExtendedTextBuilder
